import React, { useState } from 'react';
import { AccountCircle as AccountCircleIcon, Notifications as NotificationsIcon } from '@mui/icons-material';
import {
  AppBar,
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  ListSubheader,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { useAccountContext } from '../../contexts/account';
import { useNotificationsContext } from '../../contexts/notifications';
import { FetchCurrentUserQuery } from '../../generated/hooks';
import { SITE_TITLE } from '../../utils';
import SiteLogo from '../logos/SiteLogo';
import BrandMenu from './BrandMenu';
import LeagueSelector from './LeagueSelector';

type AppHeaderProps = {
  currentUser: FetchCurrentUserQuery['currentUser'];
};

function AppHeader({ currentUser }: AppHeaderProps) {
  const { logout } = useAccountContext();
  const { unreadCount } = useNotificationsContext();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement>(null);

  return (
    <AppBar position="fixed">
      <Toolbar>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          {currentUser && (
            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
              <BrandMenu />
            </Box>
          )}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
            <Typography
              variant="h6"
              component={Link}
              to="/"
              sx={{ mr: 2, display: 'flex', alignItems: 'center', color: 'inherit', textDecoration: 'none' }}
            >
              <SiteLogo sx={{ mr: 1 }} />
              {SITE_TITLE}
            </Typography>
            {currentUser && <LeagueSelector />}
          </Box>
        </Box>
        {currentUser && (
          <>
            {/* Bell lives directly in the app bar, always visible at every breakpoint -- not tucked
                behind the profile menu (notification-center-and-badge-plan.md §5.4). */}
            <IconButton component={Link} to="/notifications" sx={{ color: 'common.white', mr: 1 }} size="large">
              <Badge badgeContent={unreadCount} color="error" max={99}>
                <NotificationsIcon />
              </Badge>
            </IconButton>
            <Button
              sx={{ color: 'common.white', textTransform: 'inherit' }}
              onClick={e => setAnchorEl(e.currentTarget)}
            >
              <AccountCircleIcon sx={{ mr: 1 }} />
              <Typography variant="body1" sx={{ display: { xs: 'none', sm: 'block' } }}>
                {currentUser.fullName}
              </Typography>
            </Button>
            <Menu
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
            >
              <ListSubheader sx={{ display: { xs: 'list-item', sm: 'none' }, whiteSpace: 'nowrap' }}>
                Logged in as {currentUser.fullName}
              </ListSubheader>
              <Divider sx={{ display: { xs: 'block', sm: 'none' } }} />
              {currentUser.isSuperuser && (
                <MenuItem component={Link} to="/admin/weekly-payouts" onClick={() => setAnchorEl(null)}>
                  Weekly Payouts
                </MenuItem>
              )}
              {currentUser.isSuperuser && (
                <MenuItem component={Link} to="/admin/weekly-rankings" onClick={() => setAnchorEl(null)}>
                  Weekly Rankings
                </MenuItem>
              )}
              {currentUser.isSuperuser && (
                <MenuItem component={Link} to="/admin/manage-teams" onClick={() => setAnchorEl(null)}>
                  Manage Teams
                </MenuItem>
              )}
              <MenuItem component={Link} to="/notifications/preferences" onClick={() => setAnchorEl(null)}>
                Notification Preferences
              </MenuItem>
              <MenuItem component={Link} to="/change-password" onClick={() => setAnchorEl(null)}>
                Change Password
              </MenuItem>
              <MenuItem
                onClick={() => {
                  logout();
                  setAnchorEl(null);
                }}
              >
                Logout
              </MenuItem>
            </Menu>
          </>
        )}
      </Toolbar>
    </AppBar>
  );
}

export default AppHeader;
